/**
 * Cabeçalho da Proposta vigente (DEMO-03, telas/PainelProposta.tsx): quatro
 * blocos — Proposta, Indicadores financeiros (por tipo), Partes envolvidas e
 * Situação comercial.
 */
import type { ReactNode } from 'react';
import { SeloSituacao } from '../ui.tsx';
import { SeloTipo } from '../filtros.tsx';
import {
  SITUACOES_VERSAO,
  calculoLocacao,
  calculoTradein,
  economiaVersao,
  entidadeClienteDe,
  entidadeDe,
  gruposDaVersao,
  locaisDaVersao,
  ofertaSelecionada,
  parceiroSelecionado,
  resumoLinhas,
  type Proposta,
  type Versao,
} from '../../../../backend/src/compartilhado/propostas.ts';
import { dataBr } from '../../../../backend/src/compartilhado/oportunidades.ts';
import { useProposta } from './contexto.tsx';
import { moeda2, pct, pctNum } from './formatos.ts';

const Bloco = ({ titulo, teste, pares, extra }: { titulo: string; teste: string; pares: [string, ReactNode][]; extra?: ReactNode }) => (
  <section className="bloco-painel" data-teste={teste} aria-label={titulo}>
    <h3>{titulo}</h3>
    <dl>
      {pares.map(([k, x]) => (
        <div key={k}>
          <dt>{k}</dt>
          <dd>{x}</dd>
        </div>
      ))}
    </dl>
    {extra}
  </section>
);

export function PainelProposta({ proposta, v, teste = 'resumo-vigente', acoes }: { proposta: Proposta; v: Versao; teste?: string; acoes?: ReactNode }) {
  const { d, dados } = useProposta();
  const o = dados.oportunidade;
  const e = economiaVersao(d, v);
  const loc = calculoLocacao(v);
  const tri = calculoTradein(v);
  const r = resumoLinhas(v);
  const ent = entidadeClienteDe(d, v.entidadeClienteId)?.entidade;
  const cliente = d.clientes[0];
  const g = gruposDaVersao(d, v);
  const nomes = (ids: string[]) => ids.map((x) => d.grupos.find((y) => y.id === x)?.nome ?? '—').join(', ') || '—';
  const vigente = proposta.versoes.filter((x) => x.situacao !== 'rascunho' && x.situacao !== 'substituida').at(-1)?.numero === v.numero;
  const parceiro = parceiroSelecionado(v);
  const entParceiro = parceiro ? entidadeDe(d, parceiro) : undefined;
  const financeiro: [string, ReactNode][] =
    proposta.tipo === 'venda'
      ? [
          ['Valor de venda', moeda2(e.totalProdutos)],
          ['Custo', moeda2(e.custo)],
          ['Margem (R$)', moeda2(e.bruta)],
          ['Margem (%)', pct(e.percentual)],
          ...(e.totalFinal !== e.totalProdutos ? ([['Total final ao cliente (com frete)', moeda2(e.totalFinal)]] as [string, ReactNode][]) : []),
        ]
      : proposta.tipo === 'locacao'
        ? loc
          ? [
              ['Valor da parcela', `${moeda2(loc.clienteParcela)} (${ofertaSelecionada(v)!.periodicidade.toLowerCase()})`],
              ['Prazo contratual', `${ofertaSelecionada(v)!.prazoMeses} meses · ${loc.cobrancas} cobrança(s)`],
              ['Valor total do contrato', moeda2(loc.totalContrato)],
              ['Comissão total Inttrix', moeda2(loc.comissaoTotal)],
              ['Líquido total do parceiro', moeda2(loc.liquidoTotal)],
            ]
          : [['Proposta de parceiro', 'nenhuma selecionada']]
        : tri
          ? [
              ['Avaliação bruta total', moeda2(tri.bruto)],
              ['Valor total destinado ao Cliente', moeda2(tri.oferta)],
              ['Comissão total Inttrix', moeda2(tri.comissao)],
              ['Percentual equivalente', pctNum(tri.percentualEquivalente)],
            ]
          : [['Avaliação de comprador', 'nenhuma selecionada']];
  const partes: [string, ReactNode][] = [
    ['Cliente', cliente?.nome ?? '—'],
    ['Entidade/CNPJ', ent ? `${ent.tipo}: ${ent.razao} — ${ent.cnpj}` : '—'],
    ...(proposta.tipo === 'venda'
      ? ([
          ['Parceiro(s) comercial(is)', nomes(g.selecionados.length ? g.selecionados : g.cotados)],
          ['Entidade(s) faturadora(s)', [...new Set(v.itens.filter((i) => i.entidadeId).map((i) => entidadeDe(d, i.entidadeId)?.fantasia ?? '—'))].join(', ') || '—'],
          ['Faturar para', `${locaisDaVersao(d, v).faturamento.razao} (${locaisDaVersao(d, v).faturamento.cnpj})`],
        ] as [string, ReactNode][])
      : ([
          [proposta.tipo === 'locacao' ? 'Parceiro de Locação selecionado' : 'Parceiro de Trade-in selecionado', entParceiro ? <span data-teste="painel-parceiro-selecionado">{`${entParceiro.razao} — ${entParceiro.cnpj}`}</span> : 'nenhum selecionado'],
          ['Parceiro comercial', entParceiro ? (d.grupos.find((x) => x.id === entParceiro.grupoId)?.nome ?? '—') : '—'],
        ] as [string, ReactNode][])),
  ];
  return (
    <div className="painel-proposta" data-teste={teste}>
      <section className="bloco-painel bloco-principal" data-teste="painel-principal" aria-label="Proposta">
        <div className="linha-principal">
          <strong className="numero-proposta">Proposta {proposta.numero}</strong>
          <span className="selo selo-neutro">
            v{v.numero}
            {vigente ? ' · vigente' : ''}
          </span>
          <SeloSituacao texto={SITUACOES_VERSAO[v.situacao]} tom={v.situacao === 'substituida' ? 'encerrado' : v.situacao.startsWith('aceita') ? 'positivo' : 'aberto'} />
          <SeloTipo tipo={proposta.tipo} />
          <span className="espaco" />
          {acoes}
        </div>
      </section>
      <Bloco titulo="Indicadores financeiros" teste="painel-financeiro" pares={financeiro} />
      <Bloco titulo="Partes envolvidas" teste="painel-partes" pares={partes} />
      <Bloco
        titulo="Situação comercial"
        teste="painel-situacao"
        pares={[
          ['Validade', dataBr(v.validade)],
          ['Previsão de fechamento', dataBr(o.previsaoFechamento)],
          ['Linhas', String(r.linhas)],
          ['Resultado', <span data-teste="vigente-linhas">{`${r.ganhos} ganha(s) · ${r.parciais} parcial(is) · ${r.perdidos} perdida(s) · ${r.pendentes} pendente(s)`}</span>],
        ]}
      />
    </div>
  );
}
